import type { CSSProperties, ReactNode } from "react";
import type { MfitGuideText } from "../../scenario/types";
import "./mfit.css";

/** What an mfit device reports when it ends its beat. App records the event
 *  (risk or positive, looked up in the scenario) and follows `key`. */
export interface MfitDone {
  key?: string;
  eventId?: string;
  evidence?: string;
  /** several events at once (recorded in order). */
  events?: { eventId: string; evidence?: string }[];
}

export type MfitGuideFn = (g: MfitGuideText | null) => void;

export function Avatar({ name, initials, url, size = 32, tone }: { name: string; initials?: string; url?: string; size?: number; tone?: string }) {
  const ini =
    initials ??
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("");
  return (
    <span
      className="mfit-av"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: tone } as CSSProperties}
      aria-hidden="true"
    >
      {url ? <img src={url} alt="" draggable={false} /> : ini}
    </span>
  );
}

/** Landscape iPad shell. Glued to the 3D tablet while it is small; centred and
 *  readable once the learner has opened it. */
export function IpadFrame({
  glueStyle,
  expanded,
  clock,
  children,
  className,
}: {
  glueStyle?: CSSProperties;
  expanded: boolean;
  clock?: string;
  children: ReactNode;
  className?: string;
}) {
  if (!expanded) {
    return (
      <div className={`mfit-ipad mfit-ipad--glued${className ? ` ${className}` : ""}`} style={glueStyle}>
        {children}
      </div>
    );
  }
  return (
    <div className="mfit-ipad-stage">
      <div className={`mfit-ipad mfit-ipad--open${className ? ` ${className}` : ""}`}>
        <div className="mfit-ipad-bar" aria-hidden="true">
          <span>{clock ?? ""}</span>
          <span className="mfit-ipad-bar-r">
            <i className="mfit-ic-wifi" /> 82%
            <i className="mfit-ic-batt" />
          </span>
        </div>
        <div className="mfit-ipad-screen">{children}</div>
      </div>
    </div>
  );
}

export function InfoRows({ rows }: { rows: { label: string; value: string; warn?: boolean }[] }) {
  return (
    <dl className="mfit-rows">
      {rows.map((r) => (
        <div key={r.label} className={`mfit-row${r.warn ? " is-warn" : ""}`}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
